import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  Compass,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Search,
  Shield,
  Sparkles,
  User as UserIcon,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

export default function Navbar() {
  const { user, loginWithDiscord, logout } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile sheet whenever we navigate — otherwise it stays open behind the new page.
  useEffect(() => {
    const close = () => setMobileOpen(false);
    window.addEventListener("popstate", close);
    return () => window.removeEventListener("popstate", close);
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/servers?search=${encodeURIComponent(q.trim())}`);
      setMobileOpen(false);
    }
  };

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const linkCls = ({ isActive }) =>
    `text-sm font-medium tracking-wide transition-colors ${
      isActive ? "text-white" : "text-zinc-400 hover:text-white"
    }`;

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-6 lg:gap-8 min-w-0">
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="logo-link">
            <img
              src="/logo.svg"
              alt="Serverlistr"
              width="36"
              height="36"
              className="w-9 h-9 rounded-xl shadow-lg shadow-blurple/30 group-hover:shadow-blurple/50 transition-shadow"
            />
            <span className="font-heading font-black text-lg tracking-tight">
              Serverlistr
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <NavLink to="/servers" className={linkCls} data-testid="nav-browse">
              <span className="inline-flex items-center gap-1.5"><Compass className="w-4 h-4" />Browse</span>
            </NavLink>
            <NavLink to="/servers/featured" className={linkCls} data-testid="nav-featured">
              <span className="inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" />Featured</span>
            </NavLink>
            <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">
              <span className="inline-flex items-center gap-1.5">Pricing</span>
            </NavLink>
          </nav>
        </div>

        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search Discord servers, tags, communities..."
              className="w-full h-10 pl-10 pr-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple transition-all"
            />
          </div>
        </form>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Search icon shortcut on mobile — jumps straight to browse with focus. */}
          <button
            type="button"
            data-testid="mobile-search-btn"
            onClick={() => navigate("/servers")}
            aria-label="Search"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            <Search className="w-5 h-5" />
          </button>

          {user ? (
            <>
              <button
                data-testid="add-server-btn"
                onClick={() => navigate("/submit")}
                className="hidden sm:inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)] active:scale-95"
              >
                <Plus className="w-4 h-4" /> Add Server
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-btn" className="flex items-center gap-2 p-1 rounded-full hover:bg-white/5 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blurple flex items-center justify-center">
                        <UserIcon className="w-4 h-4" />
                      </div>
                    )}
                    {(user.role === "admin" || user.role === "owner") && (
                      <span
                        data-testid="staff-badge"
                        className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-black tracking-widest text-neon-pink bg-neon-pink/10 border border-neon-pink/30"
                      >
                        <Shield className="w-2.5 h-2.5" />
                        {user.role === "owner" ? "OWNER" : "STAFF"}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#0A0A0F] border-white/10 text-white min-w-[200px]">
                  <DropdownMenuLabel className="text-xs text-zinc-400">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-white/5">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-submit" onClick={() => navigate("/submit")} className="cursor-pointer focus:bg-white/5">
                    <Plus className="w-4 h-4 mr-2" /> Submit Server
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/5 text-neon-pink">
                      <Shield className="w-4 h-4 mr-2" /> Staff console
                    </DropdownMenuItem>
                  )}
                  {user.role === "owner" && (
                    <>
                      <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")} className="cursor-pointer focus:bg-white/5 text-neon-pink">
                        <Shield className="w-4 h-4 mr-2" /> Staff console
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-owner-apps" onClick={() => navigate("/owner/applications")} className="cursor-pointer focus:bg-white/5 text-neon-cyan">
                        <Shield className="w-4 h-4 mr-2" /> Staff applications
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="menu-owner-positions" onClick={() => navigate("/owner/positions")} className="cursor-pointer focus:bg-white/5 text-neon-cyan">
                        <Shield className="w-4 h-4 mr-2" /> Manage positions
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer focus:bg-white/5 text-red-400">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <button
              data-testid="login-discord-btn"
              onClick={loginWithDiscord}
              className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)] active:scale-95"
            >
              <DiscordGlyph />
              <span>Sign in with Discord</span>
            </button>
          )}

          {/* Hamburger — mobile only */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-btn"
                aria-label="Open menu"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-[#05050A] border-l border-white/10 text-white w-[86vw] sm:w-96 p-0 [&>button]:hidden"
              data-testid="mobile-menu-sheet"
            >
              <VisuallyHidden>
                <SheetTitle>Site navigation</SheetTitle>
              </VisuallyHidden>

              <div className="flex items-center justify-between h-16 px-4 border-b border-white/10">
                <span className="font-heading font-black text-lg tracking-tight">Menu</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                  className="w-10 h-10 inline-flex items-center justify-center rounded-lg text-zinc-400 hover:text-white hover:bg-white/5"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                <form onSubmit={onSearch}>
                  <label className="relative block">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      data-testid="mobile-search-input"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search Discord servers…"
                      className="w-full h-11 pl-10 pr-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
                      autoComplete="off"
                    />
                  </label>
                </form>

                <nav className="flex flex-col" data-testid="mobile-nav-links">
                  <MobileLink icon={<Compass className="w-5 h-5" />} onClick={() => go("/servers")} testId="mobile-nav-browse">
                    Browse
                  </MobileLink>
                  <MobileLink icon={<Sparkles className="w-5 h-5 text-neon-cyan" />} onClick={() => go("/servers/featured")} testId="mobile-nav-featured">
                    Featured
                  </MobileLink>
                  <MobileLink icon={<span className="w-5 h-5 inline-flex items-center justify-center text-neon-pink font-black">$</span>} onClick={() => go("/pricing")} testId="mobile-nav-pricing">
                    Pricing
                  </MobileLink>
                </nav>

                <div className="pt-4 border-t border-white/10 space-y-2">
                  {user ? (
                    <>
                      <button
                        type="button"
                        data-testid="mobile-add-server-btn"
                        onClick={() => go("/submit")}
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all"
                      >
                        <Plus className="w-4 h-4" /> Add Your Server
                      </button>
                      <button
                        type="button"
                        onClick={() => go("/dashboard")}
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold transition-all"
                      >
                        <LayoutDashboard className="w-4 h-4" /> Dashboard
                      </button>
                      {(user.role === "admin" || user.role === "owner") && (
                        <button
                          type="button"
                          onClick={() => go("/admin")}
                          className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-neon-pink/10 hover:bg-neon-pink/20 border border-neon-pink/40 text-neon-pink font-semibold transition-all"
                        >
                          <Shield className="w-4 h-4" /> Staff Console
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          logout();
                          setMobileOpen(false);
                        }}
                        className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl text-red-400 hover:bg-red-500/10 font-semibold transition-all"
                      >
                        <LogOut className="w-4 h-4" /> Log out
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      data-testid="mobile-login-discord-btn"
                      onClick={() => {
                        loginWithDiscord();
                        setMobileOpen(false);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-semibold transition-all"
                    >
                      <DiscordGlyph />
                      Sign in with Discord
                    </button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function MobileLink({ icon, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex items-center gap-3 h-12 px-3 rounded-lg text-base font-semibold text-zinc-200 hover:bg-white/5 hover:text-white transition-colors"
    >
      <span className="text-zinc-500">{icon}</span>
      {children}
    </button>
  );
}

function DiscordGlyph() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.54 0c1.356 0 2.46 1.104 2.46 2.472V24l-2.58-2.28-1.452-1.344-1.536-1.428.636 2.22H3.462A2.472 2.472 0 0 1 1 18.696V2.472C1 1.104 2.104 0 3.462 0h16.078zm-4.632 15.672c2.652-.084 3.672-1.824 3.672-1.824 0-3.864-1.728-6.996-1.728-6.996-1.728-1.296-3.372-1.26-3.372-1.26l-.168.192c2.04.624 2.988 1.524 2.988 1.524a9.75 9.75 0 0 0-3.612-1.152 10.08 10.08 0 0 0-2.436.024c-.072 0-.132.012-.204.024-.42.036-1.44.192-2.724.756-.444.204-.708.348-.708.348s.996-.948 3.156-1.572l-.12-.144s-1.644-.036-3.372 1.26c0 0-1.728 3.132-1.728 6.996 0 0 1.008 1.74 3.66 1.824 0 0 .444-.54.804-.996-1.524-.456-2.1-1.416-2.1-1.416l.336.204.048.036.047.024.144.084c.24.144.48.252.696.348.384.156.84.312 1.38.42a6.55 6.55 0 0 0 2.436.024c.396-.072.804-.18 1.224-.36.288-.132.612-.324.948-.588 0 0-.6.984-2.172 1.428.36.444.792.972.792.972zm-5.58-5.604c-.684 0-1.224.6-1.224 1.332 0 .732.552 1.332 1.224 1.332.684 0 1.224-.6 1.224-1.332.012-.732-.54-1.332-1.224-1.332zm4.38 0c-.684 0-1.224.6-1.224 1.332 0 .732.552 1.332 1.224 1.332.684 0 1.224-.6 1.224-1.332 0-.732-.54-1.332-1.224-1.332z"/>
    </svg>
  );
}
